import type {
  ExtendedConstraints,
  CapabilityConfig,
  EventHandlerCb,
} from '../../types';
import { hasOnlyKeysFromSet } from '../../utils';

export const id = 'transcription';

/**
 * Specifies what type of output the transcription should produce.
 * - 'transcript': Generate a text transcript from the audio
 * - 'audio': Provide a URL to the recorded audio file
 */
export type TranscriptionOutput = 'transcript' | 'audio';

const VALID_OUTPUT_VALUES: readonly TranscriptionOutput[] = [
  'transcript',
  'audio',
] as const;

export enum Events {
  StartStatus = 'transcription:start-status',
  StopStatus = 'transcription:stop-status',
  CancelStatus = 'transcription:cancel-status',
  CompletedStatus = 'transcription:completed-status',
  StateStatus = 'transcription:state-status',
}

export enum Actions {
  Start = 'transcription:start',
  Stop = 'transcription:stop',
  Cancel = 'transcription:cancel',
  State = 'transcription:state',
}

export type Config = CapabilityConfig & {
  type: typeof id;
  /**
   * Specifies what output types to generate from the transcription.
   * Can include 'transcript' and/or 'audio'.
   * @default ['transcript']
   */
  output?: TranscriptionOutput[];
};

export type ActionSuccessStatusPayload = {
  isSuccess: true;
  transcriptionId: string;
};

export type ActionErrorStatusPayload = {
  isSuccess: false;
  errorMessage: string;
  transcriptionId?: string;
};

export type ActionStatusPayload =
  | ActionSuccessStatusPayload
  | ActionErrorStatusPayload;

export type TranscriptUtterance = {
  /**
   * The confidence score for the transcript of this utterance
   */
  confidence: number;
  /**
   * The ending time, in milliseconds, of the utterance in the audio file
   */
  end: number;
  /**
   * The speaker of this utterance, where each speaker is assigned a sequential capital letter - e.g. "A" for Speaker A, "B" for Speaker B, etc.
   */
  speaker: string;
  /**
   * The starting time, in milliseconds, of the utterance in the audio file
   */
  start: number;
  /**
   * The text for this utterance
   */
  text: string;
};
export type CompletedPayload = {
  text: string;
  confidence?: number;
  /**
   * BCP-47 language code detected by ALD (e.g. 'en', 'es', 'fr').
   * Present when language_detection is enabled on the transcription request.
   */
  language_code?: string;
  /**
   * Confidence score (0–1) for the detected language.
   * Present when language_detection is enabled on the transcription request.
   */
  language_confidence?: number;
  utterances?: TranscriptUtterance[];
  /**
   * The recorded audio file as a Blob. Only present when 'audio' is included
   * in the output configuration. Passed through the bridge via postMessage
   * structured clone (not JSON-serializable).
   */
  audioBlob?: Blob;
};

export type CompletedStatusPayload = ActionStatusPayload & {
  transcriptionId: string;
  data?: CompletedPayload;
};

export type StateStatusPayload = {
  isSuccess: true;
  state: 'available' | 'unavailable';
};

/**
 * Optional payload for the transcription:start action.
 * Allows extensions to override the output configuration at runtime.
 * When not provided, the output from the manifest config is used.
 */
export type StartPayload = {
  output?: TranscriptionOutput[];
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;
  eventPayloadTuple:
    | [Events.StartStatus, ActionStatusPayload]
    | [Events.StopStatus, ActionStatusPayload]
    | [Events.CancelStatus, ActionStatusPayload]
    | [Events.CompletedStatus, CompletedStatusPayload]
    | [Events.StateStatus, StateStatusPayload];
  eventHandlerTuple:
    | [Events.StartStatus, EventHandlerCb<ActionStatusPayload>]
    | [Events.StopStatus, EventHandlerCb<ActionStatusPayload>]
    | [Events.CancelStatus, EventHandlerCb<ActionStatusPayload>]
    | [Events.CompletedStatus, EventHandlerCb<CompletedStatusPayload>]
    | [Events.StateStatus, EventHandlerCb<StateStatusPayload>];
  actionPayloadTuple:
    | [Actions.Start]
    | [Actions.Start, StartPayload]
    | [Actions.Stop]
    | [Actions.Cancel]
    | [Actions.State];
}>;

const VALID_CONFIG_KEYS = ['type', 'output'] as const;
export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  // Validate output array if provided
  const configWithOutput = config as Config;
  if (configWithOutput.output !== undefined) {
    if (!Array.isArray(configWithOutput.output)) {
      return false;
    }
    if (configWithOutput.output.length === 0) {
      return false;
    }
    if (
      !configWithOutput.output.every((value) =>
        VALID_OUTPUT_VALUES.includes(value),
      )
    ) {
      return false;
    }
  }

  return true;
}
